/**
 * ConfirmEmailChangePage - Email Change Confirmation Page
 *
 * Handles email change confirmation when user clicks the link in the confirmation email.
 * Calls the backend API to confirm the token and displays the result.
 */

import { useEffect, useState, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { authService } from '../../services/auth.service';

type ConfirmationStatus = 'loading' | 'success' | 'error' | 'expired';

export function ConfirmEmailChangePage() {
  const { token } = useParams<{ token: string }>();
  const { t } = useTranslation();
  const [status, setStatus] = useState<ConfirmationStatus>('loading');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [newEmail, setNewEmail] = useState<string>('');
  const confirmationAttempted = useRef(false);

  useEffect(() => {
    // Prevent double-calling in React strict mode
    if (confirmationAttempted.current) return;
    confirmationAttempted.current = true;

    const confirmEmailChange = async () => {
      if (!token) {
        setStatus('error');
        setErrorMessage(t('auth.confirmEmailChange.invalidToken'));
        return;
      }

      try {
        const response = await authService.confirmEmailChange(token);
        if (response.success && response.data) {
          setStatus('success');
          setNewEmail(response.data.newEmail);
        } else {
          const errorCode = response.error?.code;
          if (errorCode === 'TOKEN_EXPIRED' || errorCode === 'REQUEST_NOT_FOUND') {
            setStatus('expired');
            setErrorMessage(t('auth.confirmEmailChange.expired'));
          } else {
            setStatus('error');
            setErrorMessage(response.error?.message || t('auth.confirmEmailChange.error'));
          }
        }
      } catch (error: unknown) {
        const axiosError = error as { response?: { data?: { error?: { code?: string; message?: string } } } };
        const errorCode = axiosError.response?.data?.error?.code;
        if (errorCode === 'TOKEN_EXPIRED' || errorCode === 'REQUEST_NOT_FOUND') {
          setStatus('expired');
          setErrorMessage(t('auth.confirmEmailChange.expired'));
        } else {
          setStatus('error');
          setErrorMessage(axiosError.response?.data?.error?.message || t('auth.confirmEmailChange.error'));
        }
      }
    };

    confirmEmailChange();
  }, [token, t]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-violet-50 to-slate-100 dark:from-slate-900 dark:to-slate-800 px-4">
      <div className="max-w-md w-full">
        <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl p-8 text-center">
          {/* Logo */}
          <div className="mb-6">
            <div className="w-16 h-16 mx-auto bg-gradient-to-br from-violet-500 to-violet-600 rounded-2xl flex items-center justify-center shadow-lg">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
          </div>

          {/* Loading State */}
          {status === 'loading' && (
            <>
              <div className="w-12 h-12 border-4 border-violet-200 border-t-violet-600 rounded-full animate-spin mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-2">
                {t('auth.confirmEmailChange.verifying')}
              </h2>
              <p className="text-slate-500 dark:text-slate-400">
                {t('auth.confirmEmailChange.pleaseWait')}
              </p>
            </>
          )}

          {/* Success State */}
          {status === 'success' && (
            <>
              <div className="w-16 h-16 mx-auto mb-4 bg-green-100 dark:bg-green-900/40 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-2">
                {t('auth.confirmEmailChange.success')}
              </h2>
              <p className="text-slate-500 dark:text-slate-400 mb-2">
                {t('auth.confirmEmailChange.successMessage')}
              </p>
              <p className="text-violet-600 dark:text-violet-400 font-medium mb-6">
                {newEmail}
              </p>
              <p className="text-slate-500 dark:text-slate-400 text-sm mb-6">
                {t('auth.confirmEmailChange.loginWithNewEmail')}
              </p>
              <Link
                to="/auth/login"
                className="inline-flex items-center justify-center px-6 py-3 bg-violet-600 text-white font-medium rounded-lg hover:bg-violet-700 transition-colors"
              >
                {t('auth.confirmEmailChange.goToLogin')}
              </Link>
            </>
          )}

          {/* Error State */}
          {status === 'error' && (
            <>
              <div className="w-16 h-16 mx-auto mb-4 bg-red-100 dark:bg-red-900/40 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-2">
                {t('auth.confirmEmailChange.errorTitle')}
              </h2>
              <p className="text-slate-500 dark:text-slate-400 mb-6">
                {errorMessage}
              </p>
              <Link
                to="/auth/login"
                className="inline-flex items-center justify-center px-6 py-3 bg-slate-600 text-white font-medium rounded-lg hover:bg-slate-700 transition-colors"
              >
                {t('auth.confirmEmailChange.backToLogin')}
              </Link>
            </>
          )}

          {/* Expired State */}
          {status === 'expired' && (
            <>
              <div className="w-16 h-16 mx-auto mb-4 bg-amber-100 dark:bg-amber-900/40 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-amber-600 dark:text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-2">
                {t('auth.confirmEmailChange.expiredTitle')}
              </h2>
              <p className="text-slate-500 dark:text-slate-400 mb-4">
                {t('auth.confirmEmailChange.expiredMessage')}
              </p>
              <p className="text-slate-500 dark:text-slate-400 text-sm mb-6">
                {t('auth.confirmEmailChange.requestAgain')}
              </p>
              <div className="flex flex-col gap-3">
                <Link
                  to="/settings"
                  className="inline-flex items-center justify-center px-6 py-3 bg-violet-600 text-white font-medium rounded-lg hover:bg-violet-700 transition-colors"
                >
                  {t('auth.confirmEmailChange.goToSettings')}
                </Link>
                <Link
                  to="/auth/login"
                  className="text-violet-600 dark:text-violet-400 hover:underline text-sm"
                >
                  {t('auth.confirmEmailChange.backToLogin')}
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default ConfirmEmailChangePage;
